import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowTopRightOnSquareIcon, BuildingStorefrontIcon, DevicePhoneMobileIcon, LinkIcon, PlusCircleIcon } from "@heroicons/react/24/outline";

import { useTillSetup } from "../../data/useTill";
import { ITillLocationRequest, ITillSetupStatus, TillConnectMode, TillSetupStep } from "../../models/till/ITillSetup";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingWheel from "../shared/LoadingWheel";
import StatusPill, { StatusPillColour } from "../shared/StatusPill";
import { WorkflowStep, WorkflowStepper } from "../shared/Workflow";

const STEPS: { key: TillSetupStep; label: string }[] = [
  { key: "Connect", label: "Connect to Stripe" },
  { key: "Onboarding", label: "Finish Stripe onboarding" },
  { key: "Location", label: "Add your location" },
  { key: "Reader", label: "Pair your reader" },
  { key: "Ready", label: "Ready" },
];

const RETURN_URL = `${window.location.origin}/admin/till?stripe=return`;

const inputClasses = "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm";
const secondaryButton = "!bg-white !text-gray-700 border !border-gray-300 hover:!bg-gray-50";

/** "business_profile.url" → "Business profile: url", so the list reads like a to-do rather than an API dump. */
function humaniseRequirement(requirement: string) {
  const [head, ...rest] = requirement.split(".");
  const words = (s: string) => s.replace(/_/g, " ").replace(/\[\d+\]/g, "");
  const label = words(head);
  const first = label.charAt(0).toUpperCase() + label.slice(1);
  return rest.length ? `${first}: ${words(rest.join(" "))}` : first;
}

function emptyLocation(seed: ITillSetupStatus["locationSeed"]): ITillLocationRequest {
  return {
    displayName: seed?.displayName ?? "",
    line1: seed?.line1 ?? "",
    line2: seed?.line2 ?? "",
    city: seed?.city ?? "",
    postcode: seed?.postcode ?? "",
    country: seed?.country ?? "GB",
  };
}

interface TillSetupWizardProps {
  /** Open on the reader step even when setup is complete (e.g. "Pair another reader"). */
  startOnReader?: boolean;
}

/**
 * Card-payment setup for Admin → Till. The API decides which step the customer is on; this only
 * draws that step, sends the browser to Stripe when a step needs it, and refetches on return.
 */
export default function TillSetupWizard({ startOnReader = false }: TillSetupWizardProps) {
  const { status, connect, createLocation, registerReader } = useTillSetup("TillSetupWizard");
  const [searchParams, setSearchParams] = useSearchParams();
  const [returnError, setReturnError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pairingAnother, setPairingAnother] = useState(startOnReader);
  const [pairedLabel, setPairedLabel] = useState<string | null>(null);

  // Stripe sends the browser back with ?stripe=return (or error&reason=); read it once, refetch, and tidy the URL.
  useEffect(() => {
    const outcome = searchParams.get("stripe");
    if (!outcome) return;
    if (outcome === "error") setReturnError(searchParams.get("reason") || "Stripe did not complete the connection.");
    status.refetch();
    const next = new URLSearchParams(searchParams);
    next.delete("stripe");
    next.delete("reason");
    next.delete("refresh");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const data = status.data?.data;
  const apiStep: TillSetupStep = data?.step ?? "Connect";
  const step: TillSetupStep = pairingAnother && apiStep === "Ready" ? "Reader" : apiStep;
  const stepIndex = STEPS.findIndex((s) => s.key === step);

  const steps: WorkflowStep[] = STEPS.map((s, i) => ({
    label: s.label,
    state: i < stepIndex || (s.key === "Ready" && apiStep === "Ready") ? "done" : i === stepIndex ? "in_progress" : "not_started",
  }));

  const goToStripe = (mode: TillConnectMode) => {
    setActionError(null);
    setReturnError(null);
    connect.mutate(
      { mode, returnUrl: RETURN_URL },
      {
        onSuccess: (res) => {
          if (res?.success && res.data?.url) window.location.href = res.data.url;
          else setActionError(res?.message ?? "Could not start the Stripe connection.");
        },
        onError: () => setActionError("Could not start the Stripe connection."),
      }
    );
  };

  if (status.isLoading) return <LoadingWheel />;

  if (status.data && !status.data.success) {
    return <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">{status.data.message}</p>;
  }

  return (
    <div className="space-y-4">
      <WorkflowStepper title="Card payments" steps={steps} minWidth={620} bodyClassName="p-3" />

      {returnError ? <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">{returnError}</p> : null}
      {actionError ? <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">{actionError}</p> : null}
      {data?.message ? <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">{data.message}</p> : null}

      {step === "Connect" ? (
        <ConnectStep stripeConfigured={!!data?.stripeConfigured} busy={connect.isLoading} onChoose={goToStripe} />
      ) : null}

      {step === "Onboarding" ? (
        <OnboardingStep
          requirements={data?.account?.requirementsCurrentlyDue ?? []}
          busy={connect.isLoading}
          checking={status.isFetching}
          onContinue={() => goToStripe("CreateNew")}
          onCheck={() => status.refetch()}
        />
      ) : null}

      {step === "Location" ? (
        <LocationStep
          seed={data?.locationSeed}
          busy={createLocation.isLoading}
          onSubmit={(body) => {
            setActionError(null);
            createLocation.mutate(body, {
              onSuccess: (res) => { if (!res?.success) setActionError(res?.message ?? "Could not save the location."); },
              onError: () => setActionError("Could not save the location."),
            });
          }}
        />
      ) : null}

      {step === "Reader" ? (
        <ReaderStep
          locations={data?.locations ?? []}
          busy={registerReader.isLoading}
          pairedLabel={pairedLabel}
          canCancel={apiStep === "Ready"}
          onCancel={() => { setPairingAnother(false); setPairedLabel(null); }}
          onSubmit={(registrationCode, label, locationId) => {
            setActionError(null);
            setPairedLabel(null);
            registerReader.mutate(
              { registrationCode, label, locationId },
              {
                onSuccess: (res) => {
                  if (res?.success) {
                    setPairedLabel(res.data?.label || "Reader");
                    setPairingAnother(false);
                  } else {
                    setActionError(res?.message ?? "Could not pair the reader.");
                  }
                },
                onError: () => setActionError("Could not pair the reader."),
              }
            );
          }}
        />
      ) : null}

      {step === "Ready" ? (
        <ReadyStep status={data} pairedLabel={pairedLabel} onPairAnother={() => { setPairedLabel(null); setPairingAnother(true); }} />
      ) : null}
    </div>
  );
}

function ConnectStep({ stripeConfigured, busy, onChoose }: { stripeConfigured: boolean; busy: boolean; onChoose: (mode: TillConnectMode) => void }) {
  if (!stripeConfigured) return null;

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div className="flex flex-col rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-2">
          <PlusCircleIcon className="h-6 w-6 text-primary-600" />
          <h4 className="font-medium text-gray-900">Create a Stripe account for me</h4>
        </div>
        <p className="mt-2 flex-1 text-sm text-gray-600">
          Stripe sets up a new account in your business's name. You fill in your details and the bank account for payouts on
          Stripe's site and come straight back here.
        </p>
        <div className="mt-4">
          <ButtonInput label="Set up with Stripe" classes="" onClick={() => onChoose("CreateNew")} isLoading={busy} />
        </div>
      </div>
      <div className="flex flex-col rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-2">
          <LinkIcon className="h-6 w-6 text-primary-600" />
          <h4 className="font-medium text-gray-900">Connect my existing Stripe account</h4>
        </div>
        <p className="mt-2 flex-1 text-sm text-gray-600">
          Already take payments with Stripe? Keep the account, readers and payouts you have. You log in to Stripe, choose the
          account, and it is linked to Wilma.
        </p>
        <div className="mt-4">
          <ButtonInput label="Connect existing account" classes={secondaryButton} onClick={() => onChoose("UseExisting")} isLoading={busy} />
        </div>
      </div>
    </div>
  );
}

function OnboardingStep({ requirements, busy, checking, onContinue, onCheck }: { requirements: string[]; busy: boolean; checking: boolean; onContinue: () => void; onCheck: () => void }) {
  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <h4 className="font-medium text-gray-900">Stripe still needs a few things before it can take payments</h4>
      {requirements.length ? (
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700">
          {requirements.map((r) => <li key={r}>{humaniseRequirement(r)}</li>)}
        </ul>
      ) : (
        <p className="mt-2 text-sm text-gray-600">Stripe is checking the details you gave it. This usually takes a few minutes; this page checks again on its own.</p>
      )}
      <div className="mt-4 flex flex-wrap gap-3">
        <ButtonInput label="Continue on Stripe" classes="" onClick={onContinue} isLoading={busy} />
        <ButtonInput label="Check again" classes={secondaryButton} onClick={onCheck} isLoading={checking} />
      </div>
    </div>
  );
}

function LocationStep({ seed, busy, onSubmit }: { seed: ITillSetupStatus["locationSeed"]; busy: boolean; onSubmit: (body: ITillLocationRequest) => void }) {
  const [form, setForm] = useState<ITillLocationRequest>(() => emptyLocation(seed));
  const set = (key: keyof ITillLocationRequest) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const complete = form.displayName.trim() && form.line1.trim() && form.city.trim() && form.postcode.trim();

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-2">
        <BuildingStorefrontIcon className="h-6 w-6 text-primary-600" />
        <h4 className="font-medium text-gray-900">Where is the reader?</h4>
      </div>
      <p className="mt-1 text-sm text-gray-600">Stripe groups readers by location. One is enough for most yards; we have filled in your address.</p>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="block sm:col-span-2">
          <span className="text-sm font-medium text-gray-700">Location name</span>
          <input className={inputClasses} value={form.displayName} onChange={set("displayName")} placeholder="e.g. Main yard" />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Address line 1</span>
          <input className={inputClasses} value={form.line1} onChange={set("line1")} />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Address line 2</span>
          <input className={inputClasses} value={form.line2} onChange={set("line2")} />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Town or city</span>
          <input className={inputClasses} value={form.city} onChange={set("city")} />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Postcode</span>
          <input className={inputClasses} value={form.postcode} onChange={set("postcode")} />
        </label>
      </div>
      <div className="mt-4">
        <ButtonInput label="Save location" classes="" onClick={() => onSubmit({ ...form, country: form.country || "GB" })} disabled={!complete} isLoading={busy} />
      </div>
    </div>
  );
}

function ReaderStep({ locations, busy, pairedLabel, canCancel, onCancel, onSubmit }: {
  locations: ITillSetupStatus["locations"];
  busy: boolean;
  pairedLabel: string | null;
  canCancel: boolean;
  onCancel: () => void;
  onSubmit: (registrationCode: string, label: string, locationId: string | null) => void;
}) {
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [locationId, setLocationId] = useState(locations.length === 1 ? locations[0].id : "");

  useEffect(() => {
    if (locations.length === 1) setLocationId(locations[0].id);
  }, [locations]);

  const needsLocationChoice = locations.length > 1;

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-2">
        <DevicePhoneMobileIcon className="h-6 w-6 text-primary-600" />
        <h4 className="font-medium text-gray-900">Pair your card reader</h4>
      </div>
      <p className="mt-1 text-sm text-gray-600">
        Switch the reader on and connect it to your Wi-Fi. On the reader, swipe in from the right edge, open <strong>Settings</strong>,
        enter the admin PIN <strong>07139</strong> and choose <strong>Generate pairing code</strong>. It shows three words, e.g.{" "}
        <span className="font-mono">sepia-cerulean-orynx</span>. Type them here.
      </p>
      {pairedLabel ? <p className="mt-3 rounded-md bg-green-50 p-3 text-sm text-green-800">{pairedLabel} is paired.</p> : null}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Pairing code</span>
          <input className={`${inputClasses} font-mono`} value={code} onChange={(e) => setCode(e.target.value)} placeholder="sepia-cerulean-orynx" autoComplete="off" />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Name for this reader (optional)</span>
          <input className={inputClasses} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Counter reader" />
        </label>
        {needsLocationChoice ? (
          <label className="block sm:col-span-2">
            <span className="text-sm font-medium text-gray-700">Location</span>
            <select className={inputClasses} value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              <option value="">Choose a location</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.displayName || l.id}</option>)}
            </select>
          </label>
        ) : null}
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        <ButtonInput label="Pair reader" classes="" onClick={() => onSubmit(code, label, locationId || null)} disabled={!code.trim() || (needsLocationChoice && !locationId)} isLoading={busy} />
        {canCancel ? <ButtonInput label="Back" classes={secondaryButton} onClick={onCancel} disabled={busy} /> : null}
      </div>
    </div>
  );
}

function ReadyStep({ status, pairedLabel, onPairAnother }: { status: ITillSetupStatus | undefined; pairedLabel: string | null; onPairAnother: () => void }) {
  const readers = status?.readers ?? [];
  const locationName = useMemo(() => {
    const byId = new Map((status?.locations ?? []).map((l) => [l.id, l.displayName || l.id]));
    return (id?: string | null) => (id ? byId.get(id) : undefined);
  }, [status?.locations]);

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <h4 className="font-medium text-gray-900">Card payments are ready</h4>
      {pairedLabel ? <p className="mt-2 rounded-md bg-green-50 p-3 text-sm text-green-800">{pairedLabel} is paired.</p> : null}
      <ul className="mt-3 divide-y divide-gray-100">
        {readers.map((r) => (
          <li key={r.id} className="flex items-center gap-3 py-2">
            <DevicePhoneMobileIcon className="h-5 w-5 text-gray-400" />
            <span className="flex-1 text-sm text-gray-900">
              {r.label || r.id} <span className="text-gray-500">· {r.deviceType}{locationName(r.locationId) ? ` · ${locationName(r.locationId)}` : ""}</span>
            </span>
            <StatusPill text={r.status === "online" ? "Online" : "Offline"} colour={r.status === "online" ? StatusPillColour.Green : StatusPillColour.Red} />
          </li>
        ))}
      </ul>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <ButtonInput label="Pair another reader" classes={secondaryButton} onClick={onPairAnother} />
        <Link to="/till" className="inline-flex items-center gap-1 text-sm text-primary-700 hover:underline">
          Open the till <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
